// Shrink an over-sized image before it becomes someone's link-preview card.
//
// A photo straight off a phone is several MB, which WhatsApp drops outright —
// the shared link then renders with no picture. Compressing here, in the
// browser, means the stored URL is already within reach of every crawler.

import { MAX_SHARE_IMAGE_BYTES } from '@travel-planner/shared/utils/share-image';

/**
 * Longest edge a share card needs. The recommended card is 1200x630; anything
 * past 1200 is detail no crawler renders, so it is pure weight.
 */
export const SHARE_IMAGE_MAX_EDGE = 1200;

// Tried in order, first result under the ceiling wins. Stops at 0.4 — past
// that the artefacts cost more than the bytes save.
const QUALITY_LADDER = [0.82, 0.7, 0.6, 0.5, 0.4];

export type OptimizedShareImage = {
  file: File;
  originalBytes: number;
  optimizedBytes: number;
  /** False when the original was already small enough to upload untouched. */
  changed: boolean;
};

/**
 * Scale factor that brings the longest edge to {@link SHARE_IMAGE_MAX_EDGE}.
 * Never enlarges. Aspect ratio is preserved rather than cropped to 1200x630:
 * cropping decides composition on the operator's behalf, and every crawler
 * already crops to its own ratio at render time.
 */
export const shareImageScale = (width: number, height: number): number => {
  const longest = Math.max(width, height);
  return longest > SHARE_IMAGE_MAX_EDGE ? SHARE_IMAGE_MAX_EDGE / longest : 1;
};

/** Whether this file needs any work at all. */
export const needsShareImageOptimization = (file: File): boolean =>
  file.size > MAX_SHARE_IMAGE_BYTES;

const toBlob = (
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });

/**
 * Re-encode `file` until it fits under the share-image ceiling.
 *
 * Returns the original untouched when it already fits, or when the browser
 * cannot decode it — a failure here should cost the operator a warning, not
 * their upload.
 */
export async function optimizeShareImage(
  file: File
): Promise<OptimizedShareImage> {
  const unchanged: OptimizedShareImage = {
    file,
    originalBytes: file.size,
    optimizedBytes: file.size,
    changed: false,
  };

  if (!needsShareImageOptimization(file)) return unchanged;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return unchanged;
  }

  try {
    const scale = shareImageScale(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return unchanged;

    // JPEG has no alpha: without a ground, a transparent PNG encodes its
    // transparent pixels black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    let smallest: Blob | null = null;
    for (const quality of QUALITY_LADDER) {
      const blob = await toBlob(canvas, quality);
      if (!blob) continue;
      if (!smallest || blob.size < smallest.size) smallest = blob;
      if (blob.size <= MAX_SHARE_IMAGE_BYTES) break;
    }

    // Even the floor of the ladder can miss on a huge, noisy photo. Upload the
    // smallest attempt anyway — it is still far better than the original.
    if (!smallest || smallest.size >= file.size) return unchanged;

    return {
      file: new File([smallest], toJpegName(file.name), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      }),
      originalBytes: file.size,
      optimizedBytes: smallest.size,
      changed: true,
    };
  } finally {
    bitmap.close();
  }
}

const toJpegName = (name: string): string =>
  `${name.replace(/\.[^.]+$/, '')}.jpg`;
